import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showSuccess, showError } from '../utils/alerts';
import { useAuth } from '../context/AuthContext';
import { companyService } from '../services/companyService';
import UserManager from '../components/UserManager';
import EmailConfiguration from '../components/EmailConfiguration';
import { usePermissions } from '../hooks/usePermissions';
import { useTheme } from '../context/ThemeContext';

interface CompanySettingsFormData {
  name: string;
  address: string;
  phone: string;
  email: string;
  logo_shape: 'square' | 'circle';
  logo_zoom: number;
}

const validationSchema = yup.object({
  name: yup.string().required('El nombre de la empresa es requerido').min(2, 'El nombre debe tener al menos 2 caracteres'),
  address: yup.string().required('La dirección es requerida'),
  phone: yup.string().required('El teléfono es requerido'),
  email: yup.string().email('Email inválido').required('El email es requerido'),
  logo_shape: yup.string().oneOf(['square', 'circle']).default('square').required('La forma del logo es requerida'),
  logo_zoom: yup.number().min(50).max(150).default(100).required('El zoom del logo es requerido')
});

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { canManageUsers } = usePermissions();
  const { currentTheme, setTheme, availableThemes } = useTheme();
  const queryClient = useQueryClient();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [faviconPreview, setFaviconPreview] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<CompanySettingsFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      address: '',
      phone: '',
      email: '',
      logo_shape: 'square',
      logo_zoom: 100
    }
  });

  // Get company settings
  const { data: companySettings, isLoading } = useQuery({
    queryKey: ['company-settings'],
    queryFn: companyService.getCompanySettings,
    enabled: !!user
  });

  // Effect to load data into form when company settings are loaded
  useEffect(() => {
    if (companySettings) {
      // Ensure logo_shape is a valid value
      const logoShape = companySettings.logo_shape === 'circle' ? 'circle' : 'square';

      reset({
        name: companySettings.name || '',
        address: companySettings.address || '',
        phone: companySettings.phone || '',
        email: companySettings.email || '',
        logo_shape: logoShape,
        logo_zoom: companySettings.logo_zoom || 100
      });

      if (companySettings.logo) {
        setLogoPreview(companyService.getFileUrl(companySettings.logo));
      }

      if (companySettings.favicon) {
        setFaviconPreview(companyService.getFileUrl(companySettings.favicon));
      }
    }
  }, [companySettings, reset]);

  // Update company settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: companyService.updateCompanySettings,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      showSuccess('Configuración actualizada exitosamente');
      
      // Update document title
      companyService.updateDocumentTitle(data.name);
    },
    onError: (error: any) => {
      const message = error.message || 'Error al actualizar la configuración';
      showError(message);
    }
  });

  // Upload logo mutation
  const uploadLogoMutation = useMutation({
    mutationFn: companyService.uploadLogo,
    onSuccess: (logoUrl) => {
      setLogoPreview(companyService.getFileUrl(logoUrl));
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      showSuccess('Logo actualizado exitosamente');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Error al subir el logo';
      showError(message);
    }
  });

  // Upload favicon mutation
  const uploadFaviconMutation = useMutation({
    mutationFn: companyService.uploadFavicon,
    onSuccess: (faviconUrl) => {
      const fullUrl = companyService.getFileUrl(faviconUrl);
      setFaviconPreview(fullUrl);
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      
      // Update favicon in document
      companyService.updateFavicon(faviconUrl);
      
      showSuccess('Favicon actualizado exitosamente');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Error al subir el favicon';
      showError(message);
    }
  });

  const onSubmit = async (data: CompanySettingsFormData) => {
    await updateSettingsMutation.mutateAsync(data);
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file
      const errors = companyService.validateImageFile(file, 5 * 1024 * 1024);
      if (errors.length > 0) {
        showError(errors[0]);
        return;
      }

      // Show preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload file
      uploadLogoMutation.mutate(file);
    }
  };

  const handleFaviconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file
      const errors = companyService.validateImageFile(file, 1 * 1024 * 1024);
      if (errors.length > 0) {
        showError(errors[0]);
        return;
      }

      // Show preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setFaviconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload file
      uploadFaviconMutation.mutate(file);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Configuración de Empresa</h1>
        <p className="text-gray-600 mt-2">Personaliza la información y apariencia de tu empresa</p>
      </div>

      {/* Preview Section */}
      <div className="bg-blue-50 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Vista Previa</h3>
        <p className="text-blue-700 text-sm mb-4">
          Así se verá tu empresa en el sistema:
        </p>
        
        {/* Sidebar Preview */}
        <div className="bg-white rounded-lg p-4 border-2 border-blue-200 mb-4">
          <div className="text-sm text-gray-600 mb-2 font-medium">Sidebar del Sistema:</div>
          <div className="flex items-center space-x-3">
            {logoPreview && (
              <div className={`w-8 h-8 overflow-hidden flex items-center justify-center ${watch('logo_shape') === 'circle' ? 'rounded-full' : 'rounded'}`}>
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="w-full h-full object-cover"
                  style={{ transform: `scale(${(watch('logo_zoom') || 100) / 100})` }}
                />
              </div>
            )}
            <span className="text-xl font-bold text-gray-900">
              {watch('name') || companySettings?.name || 'Nombre de la Empresa'}
            </span>
          </div>
        </div>

        {/* Browser Tab Preview */}
        <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
          <div className="text-sm text-gray-600 mb-2 font-medium">Pestaña del Navegador:</div>
          <div className="flex items-center space-x-2 bg-gray-100 rounded px-3 py-2">
            {faviconPreview ? (
              <img src={faviconPreview} alt="Favicon preview" className="w-4 h-4 object-contain" />
            ) : (
              <div className="w-4 h-4 bg-gray-400 rounded-sm"></div>
            )}
            <span className="text-sm font-medium text-gray-700">
              {watch('name') || companySettings?.name || 'Nombre de la Empresa'} - Sistema Empresarial
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Company Information Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Información de la Empresa</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">
                    Nombre de la Empresa <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('name')}
                    className="form-input"
                    placeholder="Ej: Mi Empresa S.A."
                  />
                  {errors.name && (
                    <p className="form-error">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    className="form-input"
                    placeholder="contacto@miempresa.com"
                  />
                  {errors.email && (
                    <p className="form-error">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    Teléfono <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('phone')}
                    className="form-input"
                    placeholder="+507 6000-0000"
                  />
                  {errors.phone && (
                    <p className="form-error">{errors.phone.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    Dirección <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('address')}
                    className="form-input"
                    placeholder="Ciudad, País"
                  />
                  {errors.address && (
                    <p className="form-error">{errors.address.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Logo Section */}
            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Logo de la Empresa</h2>
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className={`w-32 h-32 border-2 border-dashed border-gray-300 ${watch('logo_shape') === 'circle' ? 'rounded-full' : 'rounded-lg'} overflow-hidden flex items-center justify-center bg-gray-50`}>
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-full h-full object-cover"
                        style={{ transform: `scale(${(watch('logo_zoom') || 100) / 100})` }}
                      />
                    ) : (
                      <div className="text-center">
                        <svg className="w-8 h-8 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-xs text-gray-500 mt-1">Sin logo</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadLogoMutation.isPending}
                    className="btn-outline"
                  >
                    {uploadLogoMutation.isPending ? 'Subiendo...' : 'Cambiar Logo'}
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    Formatos admitidos: JPG, PNG, GIF. Tamaño máximo: 5MB.
                    <br />
                    Recomendado: 200x200px para mejor calidad.
                  </p>

                  {/* Logo Shape Selector */}
                  <div className="mt-6">
                    <label className="form-label">
                      Forma del Logo <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      Selecciona cómo quieres que se muestre tu logo en todas las pantallas del sistema.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          watch('logo_shape') === 'square'
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          const event = { target: { name: 'logo_shape', value: 'square' } };
                          register('logo_shape').onChange(event);
                        }}
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <div className="w-16 h-16 bg-gray-300 rounded flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900">Cuadrado</span>
                        </div>
                        {watch('logo_shape') === 'square' && (
                          <div className="absolute top-2 right-2">
                            <div className="w-3 h-3 bg-primary-500 rounded-full flex items-center justify-center">
                              <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>

                      <div
                        className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          watch('logo_shape') === 'circle'
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          const event = { target: { name: 'logo_shape', value: 'circle' } };
                          register('logo_shape').onChange(event);
                        }}
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900">Circular</span>
                        </div>
                        {watch('logo_shape') === 'circle' && (
                          <div className="absolute top-2 right-2">
                            <div className="w-3 h-3 bg-primary-500 rounded-full flex items-center justify-center">
                              <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {errors.logo_shape && (
                      <p className="form-error mt-2">{errors.logo_shape.message}</p>
                    )}
                  </div>

                  {/* Logo Zoom Slider */}
                  <div className="mt-6">
                    <label className="form-label">
                      Ajuste de Zoom del Logo <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      Ajusta el tamaño de tu logo dentro del contenedor. Mueve el deslizador para acercar o alejar la imagen.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-600 w-12">50%</span>
                        <input
                          type="range"
                          min="50"
                          max="150"
                          step="1"
                          {...register('logo_zoom')}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <span className="text-sm text-gray-600 w-12">150%</span>
                      </div>
                      <div className="text-center">
                        <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                          {watch('logo_zoom')}%
                        </span>
                      </div>
                    </div>
                    {errors.logo_zoom && (
                      <p className="form-error mt-2">{errors.logo_zoom.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Favicon Section */}
            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Favicon (Icono del Navegador)</h2>
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                    {faviconPreview ? (
                      <img 
                        src={faviconPreview} 
                        alt="Favicon preview" 
                        className="w-full h-full object-contain rounded-lg"
                      />
                    ) : (
                      <div className="text-center">
                        <svg className="w-6 h-6 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex-1">
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFaviconChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={uploadFaviconMutation.isPending}
                    className="btn-outline"
                  >
                    {uploadFaviconMutation.isPending ? 'Subiendo...' : 'Cambiar Favicon'}
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    Formatos admitidos: ICO, PNG. Tamaño máximo: 1MB.
                    <br />
                    Recomendado: 32x32px o 16x16px.
                  </p>
                </div>
              </div>
            </div>

            {/* Theme Configuration Section */}
            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Configuración de Tema</h2>
              <div className="space-y-4">
                <div>
                  <label className="form-label">Tema de Color</label>
                  <p className="text-sm text-gray-600 mb-3">
                    Selecciona el tema de colores que prefieras para la aplicación. El cambio se aplicará inmediatamente en todas las páginas.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {availableThemes.map((themeOption) => (
                      <div
                        key={themeOption.name}
                        className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          currentTheme === themeOption.name
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setTheme(themeOption.name as any)}
                      >
                        <div className="flex flex-col items-center space-y-2">
                            <div className="flex space-x-1">
                              <div
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: themeOption.colors.primary[500] }}
                              ></div>
                              <div
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: themeOption.colors.primary[600] }}
                              ></div>
                              <div
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: themeOption.colors.primary[700] }}
                              ></div>
                            </div>
                          <span className="text-sm font-medium text-gray-900">
                            {themeOption.label}
                          </span>
                        </div>
                        {currentTheme === themeOption.name && (
                          <div className="absolute top-2 right-2">
                            <div className="w-3 h-3 bg-primary-500 rounded-full flex items-center justify-center">
                              <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="border-t pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary"
              >
                {isSubmitting ? 'Guardando...' : 'Guardar Configuración'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Email Configuration Section */}
      <EmailConfiguration />

      {/* User Management Section */}
      {canManageUsers() && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <UserManager />
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;